import { makeAutoObservable, runInAction } from 'mobx';
import { db } from '@stores/environments/Database.web';
import { ref, get, set, onValue, off } from 'firebase/database';

interface AdminAccess {
  [userUid: string]: boolean;
}

class AdminAccessStore {
  adminAccess: AdminAccess = {};
  loading: boolean = false;
  private unsubscribe?: () => void;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Inicializa o listener para monitorar mudanças no rules/auth
   */
  init = () => {
    this.subscribeToAdminAccess();
  };

  /**
   * Limpa o listener
   */
  dispose = () => {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  };

  /**
   * Subscreve às regras de acesso admin do Firebase
   */
  private subscribeToAdminAccess = () => {
    this.unsubscribe?.(); // Cancela listener anterior se existir

    const adminRef = ref(db, 'rules/auth');
    
    this.unsubscribe = onValue(adminRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        runInAction(() => {
          this.adminAccess = data || {};
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.adminAccess = {};
          this.loading = false;
        });
      }
    }, (error) => {
      console.error('Erro ao monitorar acesso admin:', error);
      runInAction(() => {
        this.loading = false;
      });
    });
  };

  /**
   * Verifica se um usuário tem acesso admin
   * @param userUid - UID do usuário
   * @returns boolean - true se tem acesso, false caso contrário
   */
  hasAdminAccess = (userUid: string): boolean => {
    return this.adminAccess[userUid] === true;
  };

  /**
   * Altera o acesso admin de um usuário
   * @param userUid - UID do usuário
   * @param hasAccess - true para dar acesso, false para remover
   */
  setAdminAccess = async (userUid: string, hasAccess: boolean): Promise<boolean> => {
    try {
      const adminRef = ref(db, `rules/auth/${userUid}`);
      
      if (hasAccess) {
        await set(adminRef, true);
      } else {
        await set(adminRef, null); // Remove o nó
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao alterar acesso admin:', error);
      return false;
    }
  };

  /**
   * Toggle do acesso admin de um usuário
   * @param userUid - UID do usuário
   */
  toggleAdminAccess = async (userUid: string): Promise<boolean> => {
    const currentAccess = this.hasAdminAccess(userUid);
    return await this.setAdminAccess(userUid, !currentAccess);
  };
}

export const adminAccessStore = new AdminAccessStore();
